import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { mergeMap, map, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { VisitActions } from './visits.actions';
import { VisitService } from '../../services/visit.service';
import { WorkOrderActions } from '../work-orders/work-orders.actions';

@Injectable()
export class VisitsEffects {
  private actions$ = inject(Actions);
  private svc = inject(VisitService);

  loadVisits$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VisitActions.loadVisits),
      mergeMap(({ orderId, workOrderId }) => this.svc.getAll({ orderId, workOrderId }).pipe(
        map(visits => VisitActions.loadVisitsSuccess({ visits })),
        catchError(err => of(VisitActions.loadVisitsFailure({ error: err.message })))
      ))
    )
  );

  loadVisit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VisitActions.loadVisit),
      mergeMap(({ id }) => this.svc.getById(id).pipe(
        map(visit => VisitActions.loadVisitSuccess({ visit })),
        catchError(err => of(VisitActions.visitFailure({ error: err.message })))
      ))
    )
  );

  createVisit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VisitActions.createVisit),
      mergeMap(({ dto }) => this.svc.create(dto).pipe(
        map(visit => VisitActions.createVisitSuccess({ visit })),
        catchError(err => of(VisitActions.visitFailure({ error: err.message })))
      ))
    )
  );

  startVisit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VisitActions.startVisit),
      mergeMap(({ id }) => this.svc.start(id).pipe(
        map(visit => VisitActions.startVisitSuccess({ visit })),
        catchError(err => of(VisitActions.visitFailure({ error: err.message })))
      ))
    )
  );

  completeVisit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VisitActions.completeVisit),
      mergeMap(({ id, dto }) => this.svc.complete(id, dto).pipe(
        map(visit => VisitActions.completeVisitSuccess({ visit })),
        catchError(err => of(VisitActions.visitFailure({ error: err.message })))
      ))
    )
  );

  cancelVisit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VisitActions.cancelVisit),
      mergeMap(({ id }) => this.svc.cancel(id).pipe(
        map(visit => VisitActions.cancelVisitSuccess({ visit })),
        catchError(err => of(VisitActions.visitFailure({ error: err.message })))
      ))
    )
  );

  skipVisit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VisitActions.skipVisit),
      mergeMap(({ id, reason }) => this.svc.skip(id, reason).pipe(
        map(visit => VisitActions.skipVisitSuccess({ visit })),
        catchError(err => of(VisitActions.visitFailure({ error: err.message })))
      ))
    )
  );

  // Skipping can revert the parent Order's status and always changes the parent WorkOrder's
  // AdjudicatedVisitCount (Phase 2 progress chip) — refresh both so the store stays authoritative.
  refreshAfterSkip$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VisitActions.skipVisitSuccess),
      map(({ visit }) => WorkOrderActions.loadWorkOrder({ id: visit.workOrderId }))
    )
  );
}
